/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  FETCH_ACES_VERIFICATIONS,
  GET_ACES_VERIFICATIONS_BY_VERIFICATION_ID,
  POST_ACES_VERIFICATIONS_LINK_FILE,
  DELETE_ACES_VERIFICATIONS_FILE_LINKS_BY_LINK_ID,
  POST_ACES_VERIFICATIONS_CREATE_CASEWORKER_TASK,
  SET_DOCUMENTS_SUBMITTED,
  SET_FORM,
} from './actionTypes';

import {
  fetchAcesVerificationsUtil,
  getAcesVerificationsByVerificationIdUtil,
  postAcesVerificationsLinkFileUtil,
  deleteAcesVerificationsFileLinksByLinkIdUtil,
  postAcesVerificationsCreateCaseworkerTaskUtil,
} from './utils';

/**
 * Contains the action creator functions that update the Verifications slice of the store.
 *
 * @namespace VerificationsActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_verifications
   *
   * @param {string} caseId.
   * @param {string} personId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static fetchAcesVerifications = (dispatch, caseId, personId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchAcesVerificationsUtil.bind(null, caseId, personId),

      FETCH_ACES_VERIFICATIONS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchAcesVerifications.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetFetchAcesVerifications = (dispatch, data) => {
    const json = {
      type: FETCH_ACES_VERIFICATIONS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchAcesVerifications.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetFetchAcesVerificationsError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_ACES_VERIFICATIONS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_verifications/{verificationId}
   *
   * @param {string} verificationId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static getAcesVerificationsByVerificationId = (dispatch, verificationId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getAcesVerificationsByVerificationIdUtil.bind(null, verificationId),

      GET_ACES_VERIFICATIONS_BY_VERIFICATION_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getAcesVerificationsByVerificationId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetGetAcesVerificationsByVerificationId = (dispatch, data) => {
    const json = {
      type: GET_ACES_VERIFICATIONS_BY_VERIFICATION_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getAcesVerificationsByVerificationId.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetGetAcesVerificationsByVerificationIdError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_ACES_VERIFICATIONS_BY_VERIFICATION_ID.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_verifications/link_file
   *
   * @param {NewSPMVerificationFileLinkCreation} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static postAcesVerificationsLinkFile = (
    dispatch,
    newSPMVerificationFileLinkCreation,
    callback
  ) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesVerificationsLinkFileUtil.bind(null, newSPMVerificationFileLinkCreation),
      POST_ACES_VERIFICATIONS_LINK_FILE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesVerificationsLinkFile.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetPostAcesVerificationsLinkFile = (dispatch, data) => {
    const json = {
      type: POST_ACES_VERIFICATIONS_LINK_FILE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesVerificationsLinkFile.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetPostAcesVerificationsLinkFileError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_VERIFICATIONS_LINK_FILE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_verifications/{verificationId}/file_links/{linkId}
   *
   * @param {string} verificationId.
   * @param {string} linkId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static deleteAcesVerificationsFileLinksByLinkId = (
    dispatch,
    verificationId,
    linkId,
    callback
  ) => {
    ReduxUtils.generateAction(
      dispatch,
      deleteAcesVerificationsFileLinksByLinkIdUtil.bind(null, verificationId, linkId),
      DELETE_ACES_VERIFICATIONS_FILE_LINKS_BY_LINK_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to deleteAcesVerificationsFileLinksByLinkId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetDeleteAcesVerificationsFileLinksByLinkId = (dispatch, data) => {
    const json = {
      type: DELETE_ACES_VERIFICATIONS_FILE_LINKS_BY_LINK_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to deleteAcesVerificationsFileLinksByLinkId.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetDeleteAcesVerificationsFileLinksByLinkIdError = dispatch => {
    ReduxUtils.resetErrorAction(
      dispatch,
      DELETE_ACES_VERIFICATIONS_FILE_LINKS_BY_LINK_ID.failureAction
    );
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_verifications/create_caseworker_task
   *
   * @param {NewCaseParticipantEvdUploadKey} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static postAcesVerificationsCreateCaseworkerTask = (
    dispatch,
    newCaseParticipantEvdUploadKey,
    callback
  ) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesVerificationsCreateCaseworkerTaskUtil.bind(null, newCaseParticipantEvdUploadKey),
      POST_ACES_VERIFICATIONS_CREATE_CASEWORKER_TASK,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesVerificationsCreateCaseworkerTask.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetPostAcesVerificationsCreateCaseworkerTask = (dispatch, data) => {
    const json = {
      type: POST_ACES_VERIFICATIONS_CREATE_CASEWORKER_TASK.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesVerificationsCreateCaseworkerTask.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetPostAcesVerificationsCreateCaseworkerTaskError = dispatch => {
    ReduxUtils.resetErrorAction(
      dispatch,
      POST_ACES_VERIFICATIONS_CREATE_CASEWORKER_TASK.failureAction
    );
  };

  /**
   * Dispatches the data to the store.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static setDocumentsSubmitted = (dispatch, data) => {
    const json = {
      type: SET_DOCUMENTS_SUBMITTED,
      payload: data || {},
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static setForm = (dispatch, data) => {
    const json = {
      type: SET_FORM,
      payload: data || {},
    };
    dispatch(json);
  };
}
